import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { PropertyInstance, PropertyConfig, AnnotationType, PropertyHookConfig, PropertyCallbacks, PropertyType, ProjectUserPropertyConfig, ProjectGlobalPropertyConfig } from '../types';
import { PropertyValueData } from '../hooks/usePropertyManager';
import { PropertyGroup } from './PropertyGroup';
import { T } from '../../../translation/src';
import { propertyRegistry } from '../PropertyRegistry';
import {
  getGlobalPropertyService,
  hasGlobalPropertyService,
  evaluateVisibilityConditions,
  getConditionDependencyIdentifiers,
  resolveConditionDependencyIdentifiers,
  ensureFilterTogglePropertyRegistered,
  ensureClipMethodPropertyRegistered,
} from '@pointorama/potree-shared';
import type { PropertyService, Subscription } from '@pointorama/potree-shared';

/**
 * Resolve a dynamic identifier template, replacing `{objectIdentifier}` with the actual value.
 */
function resolveIdentifier(identifier: string, objectId: string): string {
  return identifier.replace('{objectIdentifier}', objectId);
}

/**
 * Resolve the storage key for a config, replacing `{objectIdentifier}` for dynamic configs.
 */
function resolveStorageKey(config: PropertyConfig, objectId: string): string {
  return config.dynamicIdentifier ? resolveIdentifier(config.identifier, objectId) : config.identifier;
}

/**
 * For dynamic identifier configs, ensure the per-instance property is registered
 * in the PropertyService so it can be read/written.
 */
function ensureDynamicPropertyRegistered(config: PropertyConfig, objectId: string): void {
  if (!config.dynamicIdentifier) return;
  const baseId = config.identifier.split('.{objectIdentifier}')[0];
  if (baseId === 'filter') {
    ensureFilterTogglePropertyRegistered(objectId);
  } else if (baseId === 'clip_method') {
    ensureClipMethodPropertyRegistered(objectId);
  }
}

/**
 * Read a property value from the reactive property service.
 * Falls back to the config default if the service isn't ready.
 */
function getReactiveValue(service: PropertyService, resolvedIdentifier: string, serviceObjectId: string, config: PropertyConfig): PropertyValueData {
  try {
    return service.getValue(resolvedIdentifier, serviceObjectId, config.type);
  } catch {
    return config.defaultValue;
  }
}

/**
 * Create a property instance reading from the reactive property service.
 * Handles single and multi-select cases, including dynamic identifiers.
 */
function createReactiveInstance(config: PropertyConfig, objectIdentifiers: string[], service: PropertyService): PropertyInstance {
  if (objectIdentifiers.length === 0) {
    return {
      config,
      value: config.defaultValue,
      isMixed: false,
    } as PropertyInstance;
  }

  const serviceObjectId = config.objectIdentifierOverride;

  if (objectIdentifiers.length === 1) {
    const objectId = objectIdentifiers[0];
    const storageKey = resolveStorageKey(config, objectId);
    const lookupObjectId = serviceObjectId ?? objectId;
    const value = getReactiveValue(service, storageKey, lookupObjectId, config);
    return {
      config,
      value: value !== undefined ? value : config.defaultValue,
      objectIdentifier: objectId,
      isMixed: false,
    } as PropertyInstance;
  }

  // Multiple objects — check if values differ
  const values = objectIdentifiers.map((objectId) => {
    const storageKey = resolveStorageKey(config, objectId);
    const lookupObjectId = serviceObjectId ?? objectId;
    return getReactiveValue(service, storageKey, lookupObjectId, config);
  });
  const firstValue = values[0];
  const isMixed = values.some((v) => JSON.stringify(v) !== JSON.stringify(firstValue));

  return {
    config,
    value: firstValue !== undefined ? firstValue : config.defaultValue,
    objectIdentifiers,
    isMixed,
  } as PropertyInstance;
}

/**
 * Helper function to filter object identifiers based on property's annotation types
 */
const getMatchingObjectIdentifiers = (propertyConfig: PropertyConfig, allObjectIdentifiers: string[], allSelectedAnnotations: Array<{ identifier: string; type: AnnotationType }>): string[] => {
  if (propertyConfig.type === PropertyType.PROJECT_USER || propertyConfig.type === PropertyType.PROJECT_GLOBAL) {
    const projectConfig = propertyConfig as ProjectUserPropertyConfig | ProjectGlobalPropertyConfig;
    if (!projectConfig.annotationTypes || projectConfig.annotationTypes.length === 0) {
      return allObjectIdentifiers;
    }

    const matchingObjects = allSelectedAnnotations.filter((annotation) => projectConfig.annotationTypes.includes(annotation.type));

    return matchingObjects.map((annotation) => annotation.identifier);
  }

  return allObjectIdentifiers;
};

interface PropertyPanelProps extends PropertyHookConfig {
  /** Panel title */
  title?: React.ReactNode;
  /** Custom CSS classes */
  className?: string;
  /** Whether the panel is disabled */
  disabled?: boolean;
  /** Whether to show validation errors */
  showErrors?: boolean;
  /** Whether to group propertys */
  groupProperties?: boolean;
  /** When true, this is the combined group/multi-select panel; properties flagged hiddenInGroup are skipped. */
  isGroupView?: boolean;
  /** Custom callbacks */
  callbacks?: PropertyCallbacks;
  /** Explicit list of selected annotations with their types.
   *  When provided, used instead of the global selection state to determine
   *  which object identifiers match each property's annotation types. */
  selectedAnnotations?: Array<{ identifier: string; type: AnnotationType }>;
  /** Filter function for instances */
  instanceFilter?: (instance: PropertyInstance) => boolean;
  /** Map of property identifiers to computed values not stored in the reactive system.
   *  Used for read-only/display properties like measurements, counts, etc. */
  computedValues?: Record<string, PropertyValueData>;
  /** Optional control rendered on the right of the first property group's header (e.g. a filter button) */
  headerAction?: React.ReactNode;
}

/**
 * Main panel component for rendering dynamic propertys.
 * Reads and writes property values through the reactive PropertyService,
 * ensuring Potree-side subscriptions fire immediately on change.
 */
const PropertyPanel_: React.FC<PropertyPanelProps> = ({ projectId, annotationTypes = [], objectIdentifiers = [], selectedAnnotations: selectedAnnotationsProp, title, className, disabled = false, showErrors = true, groupProperties = false, isGroupView = false, instanceFilter, computedValues, headerAction }) => {
  // Re-render counter — incremented when any displayed reactive property changes
  const [renderTick, setRenderTick] = useState(0);

  // Build annotation list from props — either explicitly provided or derived from objectIdentifiers + annotationTypes
  const allSelectedAnnotations = useMemo((): Array<{ identifier: string; type: AnnotationType }> => {
    if (selectedAnnotationsProp) return selectedAnnotationsProp;
    // Fallback: if only one annotation type, all objectIdentifiers share that type
    if (annotationTypes.length === 1) {
      return objectIdentifiers.map((id) => ({ identifier: id, type: annotationTypes[0] }));
    }
    // No type info available per-object — return empty so no filtering happens
    // (getMatchingObjectIdentifiers will fall through to returning all objectIdentifiers)
    return [];
  }, [selectedAnnotationsProp, objectIdentifiers, annotationTypes]);

  // Resolve which configs to display — only include properties that match the given annotation types
  const configs = useMemo((): PropertyConfig[] => {
    const all = propertyRegistry.getConfigsForAnnotationTypes(annotationTypes);
    // Deduplicate
    return all.filter((config, index, arr) => arr.findIndex((c) => c.identifier === config.identifier) === index);
  }, [annotationTypes]);

  // Build instances from reactive property values
  const propertyInstances = useMemo((): PropertyInstance[] => {
    const instances: PropertyInstance[] = [];

    for (const config of configs) {
      // Skip hidden properties
      if (config.ui?.hidden) continue;
      // Skip properties not meant for the combined group/multi-select panel
      if (isGroupView && config.ui?.hiddenInGroup) continue;

      // For readOnly properties with computedValues, create instance from computed data
      if (config.readOnly && computedValues && config.identifier in computedValues) {
        const value = computedValues[config.identifier];
        const objectId = objectIdentifiers.length === 1 ? objectIdentifiers[0] : undefined;
        instances.push({
          config,
          value,
          objectIdentifier: objectId,
          isMixed: false,
        } as PropertyInstance);
        continue;
      }

      if (!hasGlobalPropertyService()) continue;
      const service = getGlobalPropertyService();

      const matchingObjectIds = getMatchingObjectIdentifiers(config, objectIdentifiers, allSelectedAnnotations);

      if (matchingObjectIds.length > 0) {
        // For dynamic identifier configs, ensure per-instance properties are registered
        if (config.dynamicIdentifier) {
          for (const objectId of matchingObjectIds) {
            ensureDynamicPropertyRegistered(config, objectId);
          }
        }
        instances.push(createReactiveInstance(config, matchingObjectIds, service));
      }
    }
    return instances;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- renderTick forces recomputation on property changes
  }, [configs, objectIdentifiers, allSelectedAnnotations, renderTick, computedValues, isGroupView]);

  // Build a value map for visibility condition evaluation.
  // Includes both instance values and resolved condition dependency values.
  const valueMap = useMemo((): Record<string, PropertyValueData> => {
    const map: Record<string, PropertyValueData> = {};
    for (const instance of propertyInstances) {
      map[instance.config.identifier] = instance.value;
      // For dynamic identifiers, also store under the resolved storage key
      if (instance.config.dynamicIdentifier && instance.objectIdentifier) {
        const storageKey = resolveStorageKey(instance.config, instance.objectIdentifier);
        map[storageKey] = instance.value;
      }
    }
    // Also resolve condition dependency values (may be dynamic identifiers not in configs)
    if (hasGlobalPropertyService()) {
      const service = getGlobalPropertyService();
      for (const config of configs) {
        const conditions = config.ui?.visibilityConditions;
        if (!conditions) continue;
        for (const objectId of objectIdentifiers) {
          for (const resolvedId of resolveConditionDependencyIdentifiers(conditions, objectId)) {
            if (!(resolvedId in map)) {
              try {
                // Try to read the value from the service — we don't know the type, try common ones
                const depConfig = propertyRegistry.getConfig(resolvedId);
                if (depConfig) {
                  map[resolvedId] = service.getValue(resolvedId, objectId, depConfig.type);
                } else {
                  // Dynamic property (e.g. clip_method.{id}) — try PROJECT_USER
                  map[resolvedId] = service.getValue(resolvedId, 'filters', PropertyType.PROJECT_USER);
                }
              } catch {
                // Property not found
              }
            }
          }
        }
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyInstances, configs, objectIdentifiers, renderTick]);

  // Filter instances by visibility conditions
  const visibleInstances = useMemo((): PropertyInstance[] => {
    return propertyInstances.filter((instance) => evaluateVisibilityConditions(instance.config.ui?.visibilityConditions, valueMap, instance.objectIdentifier));
  }, [propertyInstances, valueMap]);

  // Collect all property identifiers referenced in visibility conditions
  // so we can subscribe to them even if they're not directly displayed
  const conditionDependencies = useMemo((): string[] => {
    const deps = new Set<string>();
    for (const config of configs) {
      for (const dep of getConditionDependencyIdentifiers(config.ui?.visibilityConditions)) {
        deps.add(dep);
      }
    }
    return Array.from(deps);
  }, [configs]);

  // Subscribe to all displayed reactive properties and condition dependencies so we re-render on changes
  useEffect(() => {
    if (!hasGlobalPropertyService()) return;
    const service = getGlobalPropertyService();
    const subscriptions: Subscription[] = [];
    const subscribed = new Set<string>();

    const subscribeTo = (identifier: string, objectId: string, propertyType: PropertyType) => {
      const key = `${identifier}:${objectId}:${propertyType}`;
      if (subscribed.has(key)) return;
      subscribed.add(key);
      try {
        const property = service.getProperty(identifier, objectId, propertyType);
        subscriptions.push(
          property.subscribeToChanges(() => {
            setRenderTick((t) => t + 1);
          }),
        );
      } catch {
        // Property service not ready for this property
      }
    };

    // Subscribe to displayed properties
    for (const config of configs) {
      if (config.readOnly) continue;
      const matchingObjectIds = getMatchingObjectIdentifiers(config, objectIdentifiers, allSelectedAnnotations);
      for (const objectId of matchingObjectIds) {
        const storageKey = resolveStorageKey(config, objectId);
        const subObjectId = config.objectIdentifierOverride ?? objectId;
        subscribeTo(storageKey, subObjectId, config.type);
      }
    }

    // Subscribe to condition dependencies (may reference properties not in configs)
    for (const depIdentifier of conditionDependencies) {
      // Check if it contains a template
      if (depIdentifier.includes('{objectIdentifier}')) {
        // Resolve for each object identifier
        for (const objectId of objectIdentifiers) {
          const resolvedId = depIdentifier.replace('{objectIdentifier}', objectId);
          const depConfig = propertyRegistry.getConfig(resolvedId);
          if (depConfig) {
            subscribeTo(resolvedId, objectId, depConfig.type);
          } else {
            // Try common property types for dynamically registered properties
            subscribeTo(resolvedId, 'filters', PropertyType.PROJECT_USER);
            subscribeTo(resolvedId, 'toggles', PropertyType.PROJECT_USER);
          }
        }
      } else {
        const depConfig = propertyRegistry.getConfig(depIdentifier);
        if (!depConfig) continue;
        const matchingObjectIds = getMatchingObjectIdentifiers(depConfig, objectIdentifiers, allSelectedAnnotations);
        for (const objectId of matchingObjectIds) {
          subscribeTo(depConfig.identifier, objectId, depConfig.type);
        }
      }
    }

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [configs, objectIdentifiers, allSelectedAnnotations, conditionDependencies]);

  // Write handler — sets reactive property value directly.
  // Uses batchUpdate for multi-object changes so Pusher sends one event instead of N.
  const handlePropertyChange = useCallback(
    (identifier: string, value: PropertyValueData, targetObjectIdentifiers?: string | string[]) => {
      if (!hasGlobalPropertyService()) return;
      const service = getGlobalPropertyService();

      const config = propertyRegistry.getConfig(identifier);
      if (!config) return;

      // Normalize target IDs
      const targetObjectIds = Array.isArray(targetObjectIdentifiers) ? targetObjectIdentifiers : targetObjectIdentifiers ? [targetObjectIdentifiers] : objectIdentifiers;

      const matchingObjectIds = getMatchingObjectIdentifiers(config, targetObjectIds, allSelectedAnnotations);
      const filteredObjectIds = targetObjectIds.filter((id) => matchingObjectIds.includes(id));

      // For user/org properties, use 'global' as identifier
      if (config.type === PropertyType.USER_SETTINGS || config.type === PropertyType.ORGANIZATION) {
        service.setValue(identifier, 'global', config.type, value);
        return;
      }

      // Single object — use setValue directly (instant, no overhead)
      if (filteredObjectIds.length <= 1) {
        for (const objectId of filteredObjectIds) {
          const storageKey = resolveStorageKey(config, objectId);
          const writeObjectId = config.objectIdentifierOverride ?? objectId;
          service.setValue(storageKey, writeObjectId, config.type, value);
        }
        return;
      }

      // Multiple objects — use batchUpdate so Pusher sends one event instead of N
      const updates = filteredObjectIds.map((objectId) => ({
        identifier: resolveStorageKey(config, objectId),
        objectIdentifier: config.objectIdentifierOverride ?? objectId,
        propertyType: config.type,
        value,
      }));
      service.batchUpdate(updates);
    },
    [objectIdentifiers, allSelectedAnnotations],
  );

  // Filter instances if filter function provided
  const filteredInstances = useMemo(() => {
    if (!instanceFilter) return visibleInstances;
    return visibleInstances.filter(instanceFilter);
  }, [visibleInstances, instanceFilter]);

  // If no instances, show empty state. When a headerAction (e.g. filter) is present,
  // keep it reachable above the message instead of the centered layout.
  if (filteredInstances.length === 0) {
    if (headerAction) {
      return (
        <div className={classNames('flex flex-col', className)}>
          <div className="mb-4 flex justify-end items-center">{headerAction}</div>
          <div className="text-sm text-gray-500">
            <T _str="No properties available" />
          </div>
        </div>
      );
    }
    return (
      <div className={classNames('flex flex-col items-center justify-center p-8 text-gray-500', className)}>
        <div className="text-sm">
          <T _str="No properties available" />
        </div>
      </div>
    );
  }

  // Render using PropertyGroup component directly
  return <PropertyGroup title={title} headerAction={headerAction} instances={filteredInstances} allInstances={filteredInstances} onPropertyChange={handlePropertyChange} className={className} disabled={disabled} showErrors={showErrors} projectId={projectId} />;
};

export const PropertyPanel = memo(PropertyPanel_);

// Convenience components for specific use cases

/**
 * Property panel for specific annotation types
 */
interface TypedPropertyPanelProps extends Omit<PropertyPanelProps, 'annotationTypes'> {
  annotationType: AnnotationType;
}

export const TypedPropertyPanel = memo(({ annotationType, ...props }: TypedPropertyPanelProps) => <PropertyPanel {...props} annotationTypes={[annotationType]} title={props.title || <T _str={`${annotationType} Properties`} />} />);
